import { api } from "../api";
import type { DesktopState } from "./notify";

/**
 * Notifications that arrive with the console closed.
 *
 * The desktop notification in notify.ts needs a live tab; on a phone there
 * almost never is one, so the same events also come as a Web Push handled by
 * the service worker. All this file does is negotiate the subscription: ask
 * the browser, hand the endpoint to the server, and take it back again.
 *
 * Every step is allowed to be unavailable — an old browser, an insecure
 * origin, a server with no VAPID pair — and the setting simply does not
 * appear. Nothing here ever throws into the settings screen.
 */

export type PushUiState = "hidden" | "unsupported" | "denied" | "off" | "on";

export function pushUiState(input: {
  /** The server has a VAPID pair (GET /agent/me/push). */
  configured: boolean;
  supported: boolean;
  permission: DesktopState;
  /** This browser holds a subscription right now. */
  subscribed: boolean;
}): PushUiState {
  if (!input.configured) return "hidden";
  if (!input.supported || input.permission === "unsupported") return "unsupported";
  if (input.permission === "denied") return "denied";
  return input.subscribed ? "on" : "off";
}

/**
 * The VAPID public key as `pushManager.subscribe` wants it.
 *
 * It arrives base64url and unpadded, and subscribe() takes raw bytes; passing
 * the string straight through fails with an InvalidCharacterError on the
 * phone, where nobody is watching a console.
 */
export function vapidKeyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function pushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    window.isSecureContext
  );
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    return (await navigator.serviceWorker.getRegistration("/app/")) ?? (await navigator.serviceWorker.ready);
  } catch {
    return null;
  }
}

/** The endpoint this browser is subscribed with, or null. */
export async function currentEndpoint(): Promise<string | null> {
  const reg = await registration();
  if (!reg) return null;
  try {
    return (await reg.pushManager.getSubscription())?.endpoint ?? null;
  } catch {
    return null;
  }
}

/**
 * Asks the browser, then tells the server.
 *
 * Returns false for every way this can decline — permission refused, no
 * service worker, the server changing its mind about push — so the caller
 * only has to decide what to draw.
 */
export async function subscribePush(publicKey: string): Promise<boolean> {
  const reg = await registration();
  if (!reg) return false;
  try {
    if (Notification.permission !== "granted" && (await Notification.requestPermission()) !== "granted") return false;
    const subscription =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        // Required by every browser that implements push: a push nobody sees
        // is not allowed, which suits us — every push here is a real message.
        userVisibleOnly: true,
        applicationServerKey: vapidKeyBytes(publicKey),
      }));
    const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return false;
    await api.subscribePush({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Tells the server first, then the browser.
 *
 * That order on purpose: a subscription dropped locally but still stored
 * would keep this server sending pushes into a void until the push service
 * finally answered 410.
 */
export async function unsubscribePush(): Promise<void> {
  const reg = await registration();
  if (!reg) return;
  try {
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return;
    await api.unsubscribePush(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  } catch {
    /* the switch goes back to off either way */
  }
}

/**
 * Takes this device off the list as part of signing out.
 *
 * Signing out closes the socket, and the server pushes to every operator
 * without one — so a subscription left behind keeps delivering customers'
 * messages to a console nobody is signed in to, on a device that may be
 * shared. It runs *before* the session ends, because telling the server needs
 * the session; and it is bounded, because `serviceWorker.ready` never settles
 * on a page whose worker did not install, and a sign-out that waits on that
 * is a sign-out that never happens.
 */
export async function forgetPushBeforeSignOut(timeoutMs = 3000): Promise<void> {
  if (!pushSupported()) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  try {
    await Promise.race([unsubscribePush(), deadline]);
  } finally {
    clearTimeout(timer);
  }
}
